/*
 * Copyright (c) 2022, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useCallback, useContext, useMemo} from 'react'
import {useCommerceAPI, BasketContext} from '../contexts'
import useCustomer from './useCustomer'
import {isError} from '../utils'
import {FAILED_BASKET_ID} from '../../constants'
import {addMasterInformation} from '../../utils/product-utils'

export default function useBasket(opts = {}) {
    const {currency} = opts
    const api = useCommerceAPI()
    const customer = useCustomer()
    const {basket, setBasket: _setBasket} = useContext(BasketContext)

    const setBasket = useCallback(
        (basketData) => {
            _setBasket((existingBasket) => {
                const _productItemsDetail = existingBasket?._productItemsDetail
                return {_productItemsDetail, ...basketData}
            })
        },
        [_setBasket]
    )

    const self = useMemo(() => {
        return {
            ...basket,

            // Check if a this represents a valid basket
            get loaded() {
                return basket && basket.basketId
            },

            get itemCount() {
                return basket?.productItems?.length || 0
            },

            /** Items taking into account quantity of each */
            get itemAccumulatedCount() {
                return basket?.productItems?.reduce((prev, next) => prev + next.quantity, 0) || 0
            },

            /** Sum of all order-level discounts */
            get totalOrderDiscount() {
                return (
                    basket?.orderPriceAdjustments?.reduce((sum, item) => {
                        return sum + item.price
                    }, 0) || 0
                )
            },

            /**
             * Get an existing basket, if basket doesn't exist, create a new one.
             *
             * NOTE: This request is the only time we are using the ShopperCustomers API
             * to interact with a customer basket. All other calls are done through the
             * ShopperBaskets API, which in our case, uses OCAPI rather than commerce sdk.
             * @param deleteOldBaskets - boolean parameter that flags cases when old baskets should be deleted from SFCC
             */
            async getOrCreateBasket(deleteOldBaskets) {
                const customerBaskets = await api.shopperCustomers.getCustomerBaskets({
                    parameters: {customerId: customer?.customerId}
                })

                // Throw if there was a problem getting the customer baskets
                if (isError(customerBaskets)) {
                    throw new Error(customerBaskets)
                }

                // We only support single baskets for now. Grab the first one.
                let basket = Array.isArray(customerBaskets?.baskets) && customerBaskets.baskets[0]

                const failedBasketId = window?.localStorage?.getItem(FAILED_BASKET_ID)
                // STOK-5330 - check and delete old failed basket
                // work-around done in order to prevent appearing of the last failed order after a successful one
                if (basket && basket.basketId === failedBasketId) {
                    const deleteResponse = await api.shopperBaskets.deleteBasket({
                        parameters: {
                            basketId: failedBasketId
                        },
                        body: {}
                    })
                    if (deleteResponse && deleteResponse.status === 204) {
                        window.localStorage.removeItem(FAILED_BASKET_ID)
                        basket = undefined
                        customerBaskets?.baskets.shift()
                    }
                }

                if (deleteOldBaskets) {
                    let basketIds = customerBaskets?.baskets?.map(
                        (currentBasket) => currentBasket.basketId
                    )
                    basketIds.splice(basketIds.indexOf(basket?.basketId), 1)
                    basketIds.forEach((id) => {
                        try {
                            api.shopperBaskets.deleteBasket({
                                parameters: {
                                    basketId: id
                                },
                                body: {}
                            })
                        } catch (e) {
                            if (process.env.NODE_ENV !== 'production') {
                                console.error('basket cannot be deleted', e)
                            }
                        }
                    })
                }

                if (!basket) {
                    // Back to using ShopperBaskets for all basket interaction.
                    basket = await api.shopperBaskets.createBasket({
                        headers: {'custom-sfdc_customer_id': api.auth.usid}
                    })

                    // Throw if there was a problem creating the basket
                    if (isError(basket)) {
                        throw new Error(basket)
                    }
                }

                // Update basket currency if it was created with the wrong one, this will also set the state.
                if (currency && basket.currency !== currency) {
                    await this.updateBasketCurrency(currency, basket.basketId)
                } else {
                    setBasket(basket)
                }

                return basket
            },

            /**
             * Update the currency of the basket
             * @param currency - The currency code.
             * @param basketID - The id of the basket.
             * @returns {Promise<void>}
             */
            async updateBasketCurrency(currency, basketId) {
                const updateBasket = await api.shopperBaskets.updateBasket({
                    body: {currency},
                    parameters: {basketId}
                })
                if (isError(updateBasket)) {
                    throw new Error(updateBasket)
                } else {
                    setBasket(updateBasket)
                }
            },

            /**
             * Update the giftMessage of the basket
             * @param {{c_giftMessageText: string, c_giftMessageSender: string, c_giftMessage: string}} giftMessage
             */
            async updateBasketGiftMessage(giftMessage) {
                const updateBasket = await api.shopperBaskets.updateBasket({
                    body: giftMessage,
                    parameters: {basketId: basket.basketId}
                })
                if (isError(updateBasket)) {
                    throw new Error(updateBasket)
                } else {
                    setBasket(updateBasket)
                }
            },

            /**
             * Add an item to the basket.
             *
             * @param {array} item
             * @param {string} item.productId - The id of the product.
             * @param {number} item.quantity - The quantity of the item.
             */
            async addItemToBasket(item) {
                const response = await api.shopperBaskets.addItemToBasket({
                    headers: {'custom-sfdc_customer_id': api.auth.usid},
                    body: item,
                    parameters: {basketId: basket.basketId}
                })
                if (response.fault) {
                    throw new Error(response)
                } else {
                    setBasket(response)
                    response.productItems.forEach((pli) => {
                        item.forEach((itemsToAdd) => {
                            if (pli.productId === itemsToAdd.productId) {
                                itemsToAdd.price = pli.price
                            }
                        })
                    })
                }
                return response
            },

            /**
             * Remove an item from the basket.
             *
             * @param {string} itemId - The id of the basket item.
             */
            async removeItemFromBasket(itemId) {
                const response = await api.shopperBaskets.removeItemFromBasket({
                    parameters: {basketId: basket.basketId, itemId: itemId}
                })
                if (response.fault) {
                    throw new Error(response)
                } else {
                    setBasket(response)
                }
            },

            /**
             * Update an item in the basket.
             *
             * @param {object} item
             * @param {string} item.productId - The id of the product.
             * @param {number} item.quantity - The quantity of the item.
             * @param {string} basketItemId - The id of the item.
             */
            async updateItemInBasket(item, basketItemId) {
                const response = await api.shopperBaskets.updateItemInBasket({
                    body: item,
                    parameters: {basketId: basket.basketId, itemId: basketItemId}
                })
                if (response.fault) {
                    throw new Error(response)
                } else {
                    setBasket(response)
                }
            },

            /**
             * Get the product information for all items in the basket.
             *
             * @param {string} ids - The id(s) of the product, separated by ",".
             * @param {boolean} options - options to pass as params to the api request
             */
            async getProductsInBasket(ids, options) {
                if (!ids) {
                    return
                }

                // https://bobbyhadz.com/blog/javascript-intermediate-value-is-not-a-function
                const itemDetail = (
                    await api.shopperProducts.getProducts({
                        parameters: {ids: ids, ...options}
                    })
                ).data.reduce((result, item) => {
                    const key = item.id
                    result[key] = item
                    return result
                }, {})

                await addMasterInformation(api, itemDetail, options)

                const updatedBasket = {
                    ...basket,
                    _productItemsDetail: {...basket?._productItemsDetail, ...itemDetail}
                }

                setBasket(updatedBasket)

                return updatedBasket
            },

            /**
             * Set the shipping address for the current basket.
             * @external Address
             * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#orderaddress
             */
            async setShippingAddress(address) {
                const response = await api.shopperBaskets.updateShippingAddressForShipment({
                    body: address,
                    parameters: {
                        basketId: basket.basketId,
                        shipmentId: 'me',
                        useAsBilling: !basket.billingAddress
                    }
                })

                setBasket(response)
            },

            /**
             * Set the shipping method for the current basket.
             *
             * @param {string} id - The id of the shipping method.
             */
            async setShippingMethod(id) {
                const response = await api.shopperBaskets.updateShippingMethodForShipment({
                    body: {id},
                    parameters: {basketId: basket.basketId, shipmentId: 'me'}
                })

                setBasket(response)
            },

            /**
             * Set the billing address for the current basket.
             * @external Address
             * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#orderaddress
             */
            async setBillingAddress(address) {
                const response = await api.shopperBaskets.updateBillingAddressForBasket({
                    body: address,
                    parameters: {basketId: basket.basketId, shipmentId: 'me'}
                })

                setBasket(response)
            },

            /**
             * Set the payment instrument for the current basket
             *
             * NOTE: API does allow adding multiple payment instruments to split payment. However,
             * we are currently only handling a single payment instrument.
             * Commerce API does not have an endpoint to edit a payment instrument, but OCAPI does.
             * We want to emulate Commerce API behavior (when using OCAPI) so we'll just remove the
             * existing payment and add the new one to simulate editing. We're making an assumption
             * that our basket will never have more than one payment instrument applied at any time.
             *
             * @external PaymentInstrument
             * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#basketpaymentinstrumentrequest
             */
            async setPaymentInstrument(paymentInstrument) {
                // Do not try to get any payment instrument in here.
                // In case of PayPal fail and retry it is possible that more than one basket (+ newly recreated one from the failed order) are active for a single customer.
                // In that case it is not possible to update the payment instruments for both of them on server side so we can only use this "work-around" here.
                // Once we create a payment we are getting the latest payment instruments for this particular basket and then we can delete useless payment instruments.
                // Another approach would be to first get the latest basket version and then get the useless payment instrument but that would mean additional call.

                // Add the new payment instrument to basket
                let response = await api.shopperBaskets.addPaymentInstrumentToBasket({
                    body: paymentInstrument,
                    parameters: {basketId: basket.basketId}
                })

                // Keep reference to old payment instrument id
                let existingPaymentInstrumentId =
                    response.paymentInstruments &&
                    response.paymentInstruments.length > 1 &&
                    response.paymentInstruments[0]?.paymentInstrumentId

                // TOOO: Handle possible error when adding payment instrument.
                // We won't attempt to remove the previous payment instrument in this case.

                // Remove the previous payment instrument if it existed
                if (existingPaymentInstrumentId) {
                    response = await api.shopperBaskets.removePaymentInstrumentFromBasket({
                        parameters: {
                            basketId: basket.basketId,
                            paymentInstrumentId: existingPaymentInstrumentId
                        }
                    })
                }

                setBasket(response)
            },

            /**
             * Remove the payment instrument for the current basket
             */
            async removePaymentInstrument() {
                let paymentInstrumentId =
                    basket.paymentInstruments && basket.paymentInstruments[0]?.paymentInstrumentId

                if (!paymentInstrumentId) {
                    return
                }

                const response = await api.shopperBaskets.removePaymentInstrumentFromBasket({
                    parameters: {
                        basketId: basket.basketId,
                        paymentInstrumentId: paymentInstrumentId
                    }
                })

                setBasket(response)
            },

            /**
             * Update the customer information for the current basket.
             *
             * @param {object} info
             * @param {string} info.email - The email of the customer.
             */
            async updateCustomerInfo(info) {
                const response = await api.shopperBaskets.updateCustomerForBasket({
                    body: info,
                    parameters: {basketId: basket.basketId}
                })

                setBasket(response)
            },

            /**
             * Apply a coupon/promo code to the current basket
             * @param {string} code - The promo code to be applied
             */
            async applyPromoCode(code) {
                const response = await api.shopperBaskets.addCouponToBasket({
                    body: {code},
                    parameters: {basketId: basket.basketId}
                })

                if (response.fault) {
                    throw new Error(response)
                }

                setBasket(response)
            },

            /**
             * Remove a coupon/promo code from the current basket
             * @param {string} couponIemId - The item id of the appied code
             */
            async removePromoCode(couponItemId) {
                const response = await api.shopperBaskets.removeCouponFromBasket({
                    parameters: {basketId: basket.basketId, couponItemId}
                })

                if (response.fault) {
                    throw new Error(response)
                }

                setBasket(response)
            },

            /**
             * Fetches and returns promo details for the given IDs
             * @param {Array<string>} ids - The promo ids to fetch
             * @returns {Object} - API response containing data
             */
            async getPromotions(ids) {
                const response = await api.shopperPromotions.getPromotions({
                    parameters: {ids: ids.join(',')}
                })

                return response
            },

            /**
             * Creates an order using the current basket.
             */
            async createOrder() {
                const sfdcCustomerId = api.auth.usid || basket.c_sfdcCustomerId
                const response = await api.shopperOrders.createOrder({
                    // We send the SLAS usid via this header. This is required by ECOM to map
                    // Einstein events sent via the API with the finishOrder event fired by ECOM
                    // when an Order transitions from Created to New status.
                    // Without this, various order conversion metrics will not appear on reports and dashboards
                    headers: {
                        _sfdc_customer_id: sfdcCustomerId,
                        'custom-sfdc_customer_id': sfdcCustomerId
                    },
                    body: {basketId: basket.basketId}
                })

                if (response.fault || (response.title && response.type && response.detail)) {
                    throw new Error(response.title)
                }

                // We replace the basket with the order result data so we can display
                // it on the confirmation page. The basket is automatically deleted
                // in SF so we need to make sure a new one is created when leaving the confirmation.
                setBasket(response)
                return response
            },

            /**
             * Fails an order using the current order instance.
             * @param {String} orderToken is the unique order token that is stored inside each SFCC order (Adyen PSP reference)
             * @param {String} errorReason is additional meta data about the reason of the order fail
             */
            async failOrder(orderToken, errorReason) {
                orderToken && window?.localStorage?.setItem(FAILED_BASKET_ID, orderToken)
                const orderNumberResponse = await api.shopperOrder.getOrderNumber({
                    orderToken: orderToken
                })
                const response = await api.shopperOrders.failOrder({
                    reason: errorReason,
                    body: {
                        orderNo: orderNumberResponse.orderNumber,
                        merchantReference: orderToken
                    }
                })

                if (response.fault || (response.title && response.type && response.detail)) {
                    throw new Error(response.title)
                }

                let recreatedBasket = response
                if (response.c_newBasketId) {
                    recreatedBasket = await api.shopperBaskets.getBasket({
                        parameters: {basketId: response.c_newBasketId}
                    })
                    if (recreatedBasket) {
                        await this.getOrCreateBasket(true)
                    }
                }

                // We are trying to recreate new basket with all the data from the order,
                // extract it and replace the old basket. In case the new basket is not created
                // we replace the basket with the order result data
                setBasket(recreatedBasket)
            },

            /**
             * Sets Adyen reference of the current order instance.
             * @param {Object} order is the object that corresponds to the SFCC order
             * @param {String} adyenReference is the value of the Adyen transaction identifier
             * @param {String} paymentMethod is the value of the Adyen payment option
             * @param {Object} paymentData is additional data for the payment option
             * @param {String} timeZone is the time zone where the order was created
             */
            async updateAdyenReference(
                order,
                adyenReference,
                paymentMethod,
                paymentData,
                timeZone
            ) {
                const response = await api.shopperOrders.updateAdyenReference({
                    adyenReference: adyenReference,
                    paymentMethod: paymentMethod,
                    paymentData: paymentData,
                    timeZone: timeZone,
                    body: order
                })

                if (response.fault || (response.title && response.type && response.detail)) {
                    throw new Error(response.title)
                }

                // We replace the basket with the order result data so we can display
                // it on the confirmation page. The basket is automatically deleted
                // in SF so we need to make sure a new one is created when leaving the confirmation.
                setBasket(response)
            },

            /**
             * Updates session data of the current order instance.
             * @param {String} sessionData is the Adyen session data that should be stored
             */
            async updateAdyenData(sessionData) {
                const response = await api.shopperOrders.updateAdyenData({
                    body: basket,
                    sessionData: sessionData
                })

                if (response.fault || (response.title && response.type && response.detail)) {
                    throw new Error(response.title)
                }

                // We replace the basket with the order result data so we can display
                // it on the confirmation page. The basket is automatically deleted
                // in SF so we need to make sure a new one is created when leaving the confirmation.
                setBasket(response)
            },

            /**
             * Updates customer identifier of the order.
             * @param {String} customerNo is the unique identifier of the SFCC customer
             */
            async updateCustomerData(customerNo, orderNo) {
                const response = await api.shopperOrders.updateCustomerData({
                    parameters: {
                        orderNo: orderNo,
                        customerNo: customerNo
                    }
                })

                if (response.fault || (response.title && response.type && response.detail)) {
                    if (process.env.NODE_ENV !== 'production') {
                        console.error('customer number cannot be set')
                    }
                }

                // We replace the current version of the order with the new one.
                // Once the response is OK we would receive different values in customer_info field or the order.
                setBasket(response)
            },

            /**
             * Cancels an order using the current order instance.
             * @param {String} orderNo is the order number of the order that should be cancelled
             * @param {String} orderToken is the unique order token that is stored inside each SFCC order (Adyen PSP reference)
             * @param {Object} cancelled order
             */
            async cancelOrder(orderNo, orderToken) {
                const response = await api.shopperOrders.cancelOrder({
                    body: {
                        orderNo: orderNo,
                        merchantReference: orderToken
                    }
                })

                return {
                    ...response,
                    _productItemsDetail: basket?._productItemsDetail
                }
            },

            /**
             * Gets order instance by passing order number.
             * @param {String} orderNo is the order number of the order instance that should be extracted
             * @returns {Object} - API response containing order object
             */
            async getOrderByNumber(orderNo) {
                const response = await api.shopperOrders.getOrder({
                    parameters: {orderNo}
                })

                // combine product details from current basket and response
                const availableProductItemsDetail = {
                    ...response._productItemsDetail,
                    ...basket?._productItemsDetail
                }

                // copied from useShopper - load missing product details
                const allImages = true
                const ids = response.productItems
                    ?.map((item) => item.productId)
                    .filter((id) => !availableProductItemsDetail[id])
                const updatedBasket = await self.getProductsInBasket(ids.toString(), {allImages})
                const _productItemsDetail = {
                    ...availableProductItemsDetail,
                    ...updatedBasket?._productItemsDetail
                }

                // inject all product details
                return {
                    ...response,
                    _productItemsDetail
                }
            },

            /**
             * Gets order instance by passing token.
             * @param {String} orderToken is the unique order token that is stored inside each SFCC order (Adyen PSP reference)
             * @returns {Object} - API response containing order object
             */
            async getOrder(orderToken) {
                const orderNumberResponse = await api.shopperOrder.getOrderNumber({
                    orderToken: orderToken
                })
                const orderNo = orderNumberResponse.orderNumber
                const response = await api.shopperOrders.getOrder({
                    parameters: {orderNo}
                })

                // combine product details from current basket and response
                const availableProductItemsDetail = {
                    ...response._productItemsDetail,
                    ...basket?._productItemsDetail
                }

                // copied from useShopper - load missing product details
                const allImages = true
                const ids = response.productItems
                    ?.map((item) => item.productId)
                    .filter((id) => !availableProductItemsDetail[id])
                const updatedBasket = await self.getProductsInBasket(ids.toString(), {allImages})
                const _productItemsDetail = {
                    ...availableProductItemsDetail,
                    ...updatedBasket?._productItemsDetail
                }

                // inject all product details
                return {
                    ...response,
                    _productItemsDetail
                }
            },

            /**
             * Fetches the applicable shipping methods for the current basket
             * @returns {Object} - API response containing data
             */
            getShippingMethods() {
                return api.shopperBaskets.getShippingMethodsForShipment({
                    parameters: {basketId: basket.basketId, shipmentId: 'me'}
                })
            },

            /**
             * Merge data from the previous shopper's basket into the current shopper's active basket
             * and delete the previous shopper's basket.
             */
            async mergeBasket() {
                const response = await api.shopperBaskets.mergeBasket({
                    headers: {
                        'Content-Type': 'application/json' // This is not required since the request has no body but CommerceAPI throws a '419 - Unsupported Media Type' error if this header is removed.
                    },
                    parameters: {
                        // the only possible solution for merging of baskets with repeating products is by passing merge mode separate_item
                        // https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-baskets?meta=mergeBasket
                        productItemMergeMode: 'separate_item',
                        createDestinationBasket: true // If the current shopper has an active basket, this parameter is ignored.
                    }
                })

                if (response.fault) {
                    throw new Error(response)
                }

                setBasket(response)
            },

            /**
             * Calls the server in order to check if the basket is valid.
             */
            async isBasketValid() {
                return await api.shopperOrder.isBasketValid({})
            }
        }
    }, [api, basket, currency, customer, setBasket])

    return self
}
